import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { login, verifyLoginOtp, resendLoginOtp } from "../../../service/api";
import actions from "../../../redux/user/actions";
import { Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import logo2 from "../../assets/img/favicon-v4.png";
import {
  sanitizeInput,
  sanitizeEmail,
  decryptString,
} from "../../../config/validation";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import {
  initializeCSRFToken,
  setCSRFToken,
} from "../../../utils/csrfProtection";
import { SECURITY_CONFIG } from "../../../config/security";

const BRAND = "#1877F2";
const BRAND_DARK = "#1464d8";

const validationSchema = yup.object().shape({
  password: yup.string().required("Password is required."),
  email: yup
    .string()
    .email("Invalid email address.")
    .required("Email is required."),
});

const Login = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Credentials step state
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const recaptchaRef = useRef(null);

  // OTP step state
  const [otpStep, setOtpStep] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [preAuthToken, setPreAuthToken] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [rateLimitWaitTime, setRateLimitWaitTime] = useState(() => {
    const savedUntil = localStorage.getItem("otpRateLimitUntil");
    if (savedUntil) {
      const remainingMs = parseInt(savedUntil) - Date.now();
      if (remainingMs > 0) return Math.ceil(remainingMs / 1000);
      localStorage.removeItem("otpRateLimitUntil");
    }
    return 0;
  });
  const countdownRef = useRef(null);

  useEffect(() => {
    let timer;
    if (rateLimitWaitTime > 0) {
      timer = setInterval(() => {
        setRateLimitWaitTime((prev) => {
          if (prev <= 1) {
            localStorage.removeItem("otpRateLimitUntil");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [rateLimitWaitTime]);

  useEffect(() => {
    const initCSRF = async () => {
      try {
        await initializeCSRFToken();
      } catch (error) {
        // silent
      }
    };
    initCSRF();

    if (!sessionStorage.getItem("hasReloaded")) {
      sessionStorage.setItem("hasReloaded", "true");
      window.location.reload();
    }

    return () => clearInterval(countdownRef.current);
  }, []);

  const startCountdown = () => {
    setResendCooldown(30);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const dispatchSession = (data) => {
    if (data?.csrfToken) setCSRFToken(data.csrfToken);
    dispatch(actions.setToken(data?.token));
    dispatch(actions.setLoggedIn(true));
    dispatch(actions.setUser(data?.userData));
    dispatch(actions.setRole(data?.role));
    dispatch(actions.setPermission(data?.permissions || []));
    dispatch(actions.setSidebarItems(data?.sidebarItems || []));
    setFailedAttempts(0);
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  /* CREDENTIALS FORM */
  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: async (values) => {
      const requireCaptcha =
        SECURITY_CONFIG.RECAPTCHA.ENABLED || failedAttempts >= 3;
      if (requireCaptcha && !captchaToken) {
        toast.error("Please complete the CAPTCHA verification.", { toastId: 'login-captcha-error' });
        return;
      }

      setLoading(true);
      try {
        const sanitizedValues = {
          email: sanitizeEmail(values.email),
          password: values.password,
          ...(captchaToken && { captchaToken }),
        };
        const payload = { encrypt: await decryptString(sanitizedValues) };
        const res = await login(payload);

        if (res?.status) {
          if (res?.data?.requireOtp) {
            // Move to OTP step
            setPreAuthToken(res.data.preAuthToken);
            setMaskedEmail(res.data.maskedEmail);
            setOtpStep(true);
            startCountdown();
            toast.success(res?.message, { toastId: 'login-success' });
          } else {
            // Fallback: direct session (should not occur when OTP is enforced)
            dispatchSession(res?.data);
            history.push("/admin");
          }
        } else {
          toast.error(res?.message, { toastId: 'login-error' });
          handleFailedLogin();
        }
      } catch (e) {
        const errorMsg = e?.data?.message || "Login failed. Please try again.";
        toast.error(errorMsg, { toastId: 'login-error' });
        handleFailedLogin();
      } finally {
        setLoading(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setCaptchaToken(null);
        }
      }
    },
  });

  const handleFailedLogin = () => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);
    if (newAttempts >= 3) {
      toast.warning(
        "Multiple failed attempts detected. Please complete the CAPTCHA.",
        { toastId: 'login-warning' }
      );
    }
  };

  /* OTP FORM */
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otpValue)) {
      toast.error("Please enter a valid 6-digit OTP.", { toastId: 'login-otp-validation-error' });
      return;
    }
    setOtpLoading(true);
    try {
      const payload = {
        encrypt: await decryptString({ otp: otpValue, preAuthToken }),
      };
      const res = await verifyLoginOtp(payload);
      if (res?.status) {
        toast.success(res?.message, { toastId: 'login-otp-success' });
        dispatchSession(res?.data);
        history.push("/admin");
      } else {
        toast.error(res?.message, { toastId: "otp-error" });
      }
    } catch (e) {
      const msg = e?.data?.message || "OTP verification failed.";
      toast.error(msg, { toastId: "otp-error" });
      
      if (e?.status === 429) {
        const reset = e?.response?.headers?.["ratelimit-reset"];
        const retryAfter = e?.response?.headers?.["retry-after"];
        let waitMs = 60000; // 1 minute fallback
        if (retryAfter) waitMs = parseInt(retryAfter) * 1000;
        else if (reset) waitMs = Math.max(0, parseInt(reset) * 1000 - Date.now());
        
        localStorage.setItem("otpRateLimitUntil", (Date.now() + waitMs).toString());
        setRateLimitWaitTime(Math.ceil(waitMs / 1000));
      } else if (e?.status === 401) {
        // Pre-auth token expired — go back to credentials
        setOtpStep(false);
        setPreAuthToken("");
        setOtpValue("");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const payload = { encrypt: await decryptString({ preAuthToken }) };
      const res = await resendLoginOtp(payload);
      if (res?.status) {
        toast.success("OTP resent successfully!", { toastId: 'login-resend-otp-success' });
        startCountdown();
      } else {
        toast.error(res?.message, { toastId: 'login-resend-otp-error' });
      }
    } catch (e) {
      toast.error(e?.data?.message || "Failed to resend OTP.", { toastId: 'login-resend-otp-error' });
      if (e?.status === 401) {
        setOtpStep(false);
        setPreAuthToken("");
        setOtpValue("");
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpValue("");
    setPreAuthToken("");
    setMaskedEmail("");
    clearInterval(countdownRef.current);
    setResendCooldown(0);
  };

  return (
    <div style={s.root}>
      {/* ── Left panel ── */}
      <div style={s.left}>
        <div style={s.leftBubble1} />
        <div style={s.leftBubble2} />
        <div style={s.leftInner}>
          <div style={s.logoWrap}>
            <img
              src={logo2}
              alt="ParaaCrypto"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              style={s.logo}
            />
          </div>
          <h2 style={s.brand}>ParaaCrypto</h2>
          <p style={s.brandSub}>Admin Dashboard</p>
          <div style={s.divider} />
          <p style={s.tagline}>Secure · Reliable · Powerful</p>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={s.right}>
        <div style={s.card}>
          {otpStep ? (
            /* ── OTP Step ── */
            <>
              <div style={s.cardTop}>
                <img
                  src={logo2}
                  alt=""
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                  style={s.cardLogo}
                />
                <h1 style={s.cardTitle}>Verify your identity</h1>
                <p style={s.cardSub}>OTP sent to {maskedEmail}</p>
              </div>

              <form onSubmit={handleOtpSubmit}>
                <div style={s.field}>
                  <label style={s.label} htmlFor="otp">
                    Enter 6-digit OTP
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="••••••"
                    value={otpValue}
                    onChange={(e) =>
                      setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    style={{ ...s.input, letterSpacing: "0.3em", textAlign: "center" }}
                    autoFocus
                    autoComplete="one-time-code"
                  />
                </div>

                <button
                  type="submit"
                  disabled={otpLoading || rateLimitWaitTime > 0}
                  style={{ ...s.btn, ...((otpLoading || rateLimitWaitTime > 0) ? s.btnDisabled : {}) }}
                >
                  {otpLoading ? <Spinner size="sm" /> : "Verify OTP"}
                </button>
                {rateLimitWaitTime > 0 && (
                  <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "8px", textAlign: "center", fontWeight: "500" }}>
                    Too many requests. Please wait {rateLimitWaitTime}s.
                  </div>
                )}
              </form>

              <div style={{ marginTop: 16, textAlign: "center" }}>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendLoading}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: resendCooldown > 0 ? "default" : "pointer",
                    color: resendCooldown > 0 ? "#9ca3af" : BRAND,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  {resendLoading
                    ? "Resending…"
                    : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend OTP"}
                </button>
              </div>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <button
                  onClick={handleBackToLogin}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#6b7280",
                    fontSize: 13,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  ← Back to login
                </button>
              </div>
            </>
          ) : (
            /* ── Credentials Step ── */
            <>
              <div style={s.cardTop}>
                <img
                  src={logo2}
                  alt=""
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                  style={s.cardLogo}
                />
                <h1 style={s.cardTitle}>Welcome back</h1>
                <p style={s.cardSub}>Sign in to your admin account</p>
              </div>

              <form onSubmit={formik.handleSubmit} noValidate>
                {/* Email */}
                <div style={s.field}>
                  <label style={s.label} htmlFor="email">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="text"
                    autoComplete="email"
                    placeholder="admin@paraacrypto.com"
                    value={formik.values.email}
                    onChange={(e) => {
                      e.target.value = sanitizeInput(e.target.value);
                      formik.handleChange(e);
                    }}
                    style={{
                      ...s.input,
                      ...(formik.errors.email && formik.touched.email
                        ? s.inputErr
                        : {}),
                    }}
                  />
                  {formik.errors.email && formik.touched.email && (
                    <span style={s.err}>{formik.errors.email}</span>
                  )}
                </div>

                {/* Password */}
                <div style={s.field}>
                  <label style={s.label} htmlFor="password">
                    Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={formik.values.password}
                      onChange={(e) => {
                        e.target.value = sanitizeInput(e.target.value);
                        formik.handleChange(e);
                      }}
                      style={{
                        ...s.input,
                        paddingRight: 44,
                        ...(formik.errors.password && formik.touched.password
                          ? s.inputErr
                          : {}),
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={s.eyeBtn}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <FaEyeSlash size={15} />
                      ) : (
                        <FaEye size={15} />
                      )}
                    </button>
                  </div>
                  {formik.errors.password && formik.touched.password && (
                    <span style={s.err}>{formik.errors.password}</span>
                  )}
                </div>

                {/* reCAPTCHA */}
                {(SECURITY_CONFIG.RECAPTCHA.ENABLED || failedAttempts >= 3) && (
                  <div style={{ marginBottom: 20 }}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={SECURITY_CONFIG.RECAPTCHA.SITE_KEY}
                      onChange={handleCaptchaChange}
                    />
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
                >
                  {loading ? <Spinner size="sm" /> : "Sign in"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const s = {
  root: {
    display: "flex",
    minHeight: "100vh",
    fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
  },
  /* Left */
  left: {
    flex: "0 0 44%",
    background: `linear-gradient(150deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  leftBubble1: {
    position: "absolute",
    width: 340,
    height: 340,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.07)",
    top: -80,
    right: -80,
  },
  leftBubble2: {
    position: "absolute",
    width: 260,
    height: 260,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: -60,
    left: -60,
  },
  leftInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    zIndex: 1,
    padding: "48px 40px",
    textAlign: "center",
  },
  logoWrap: {
    width: 96,
    height: 96,
    borderRadius: 22,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    marginBottom: 4,
  },
  logo: {
    width: 64,
    height: 64,
  },
  brand: {
    color: "#fff",
    fontSize: 30,
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.4px",
  },
  brandSub: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 14,
    margin: 0,
    fontWeight: 500,
    letterSpacing: "0.2px",
  },
  divider: {
    width: 40,
    height: 2,
    background: "rgba(255,255,255,0.3)",
    borderRadius: 2,
    margin: "6px 0",
  },
  tagline: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 12,
    margin: 0,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
  },
  /* Right */
  right: {
    flex: 1,
    background: "#f4f6fb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 24px",
  },
  card: {
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 4px 28px rgba(0,0,0,0.08)",
    padding: "44px 40px 40px",
    width: "100%",
    maxWidth: 400,
  },
  cardTop: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 32,
  },
  cardLogo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 6px",
    letterSpacing: "-0.3px",
  },
  cardSub: {
    fontSize: 14,
    color: "#6b7280",
    margin: 0,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 18,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
  },
  input: {
    height: 44,
    padding: "0 14px",
    borderRadius: 8,
    border: "1.5px solid #e5e7eb",
    fontSize: 14,
    color: "#111827",
    outline: "none",
    background: "#fff",
    width: "100%",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  },
  inputErr: {
    borderColor: "#ef4444",
  },
  eyeBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#9ca3af",
    padding: 0,
    display: "flex",
    alignItems: "center",
  },
  err: {
    fontSize: 12,
    color: "#ef4444",
    marginTop: 4,
  },
  btn: {
    width: "100%",
    height: 46,
    marginTop: 8,
    border: "none",
    borderRadius: 8,
    background: BRAND,
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "background 0.15s",
    boxSizing: "border-box",
  },
  btnDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
};

export default Login;
